import { useMemo } from 'react';

import { GQLResponseError } from '@change-corgi/core/gqlErrors';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { NormalizedFormValues } from '#src/app/pages/petitionDetails/shared/types';
import type { SignatureUser } from '#src/app/shared/api/signature';
import type { I18nFieldValidatorResult } from '#src/app/shared/form/validators';

import { redactSensitiveSignatureData } from './redactSignatureData';
import { isSampled } from './shared/sampled';

/**
 * The shape of `final-form`'s field-level validation errors, narrowed from its own
 * `ValidationErrors` type (which types values as `AnyObject`/`any`). This form's field validators
 * are always the i18n ones (see `#src/app/shared/form/validators`), so narrow to that known shape
 * here instead of letting the `any` leak into the tracking payload.
 */
type FormValidationErrors = Readonly<Record<string, I18nFieldValidatorResult>>;
type ErrorContext = Readonly<{ submitError?: unknown; errors?: FormValidationErrors }>;
export type SignatureErrorTrackingResult = Readonly<{
	trackSignError: (formData: NormalizedFormValues, errorContext: ErrorContext) => void;
	trackDoubleOptIn: () => void;
	trackExistingEmail: (user: SignatureUser) => void;
	trackPerimeterX: () => void;
}>;

function getExistingUserState(user: SignatureUser) {
	if (user.connectedToFacebook) return 'existing_fb_connected';
	if (user.passwordSet) return 'existing_pswd';
	return 'existing_no_pswd';
}

function stringifySubmitError(submitError: unknown) {
	if (submitError instanceof GQLResponseError) {
		return JSON.stringify({
			status: submitError.status,
			result: submitError.errors,
		});
	}
	if (submitError instanceof Error) {
		return submitError.message;
	}
	if (submitError && typeof submitError === 'object' && 'status' in submitError && submitError.status) {
		const response = submitError as Response;
		return JSON.stringify({
			status: response.status,
		});
	}
	return JSON.stringify(submitError);
}

export function useSignatureErrorTracking(): SignatureErrorTrackingResult {
	const track = useTracking();
	const {
		data: {
			petition,
			featureConfigs: { signValidationErrorTrackSampleRate },
		},
	} = usePetitionDetailsPageData();

	return useMemo(
		() => ({
			trackSignError: (formData: NormalizedFormValues, { submitError, errors }: ErrorContext) => {
				if (!isSampled(signValidationErrorTrackSampleRate)) return;

				const reason =
					(errors &&
						Object.entries(errors)
							.filter((entry): entry is [string, NonNullable<I18nFieldValidatorResult>] => !!entry[1]?.key)
							.map(
								([name, error]) =>
									// `key` is an opaque TranslationKey/TranslationPluralKey (see
									// #src/app/shared/form/validators): it's a plain string at runtime, opaquely
									// typed to discourage misuse elsewhere — safe to unwrap for this tracking payload.
									`${name}: ${error.key as unknown as string}`,
							)
							.join(' | ')) ||
					undefined;

				const signaturePayload = redactSensitiveSignatureData(formData);

				void track('error', {
					type: 'sign_validation_error',
					petition_id: petition.id,
					signature: JSON.stringify(signaturePayload),
					server_error: !!submitError,
					reason,
					first_name_valid: !errors?.firstName,
					last_name_valid: !errors?.lastName,
					email_valid: !errors?.email,
					mutation_response: submitError ? stringifySubmitError(submitError) : undefined,
				});
			},
			trackDoubleOptIn: () => {
				void track('double_opt_in_required', {
					user_flow: 'sign',
					context: 'petitions_show',
				});
			},
			trackExistingEmail: (user: SignatureUser) => {
				void track('attempted_sign_with_existing_email', {
					guest_existing_user_state: getExistingUserState(user),
					connected_to_facebook: user.connectedToFacebook,
					password_set: user.passwordSet,
					context: 'petitions_show',
				});
			},
			trackPerimeterX: () => {
				void track('px_captcha_shown', { form: 'corgi_sign', petition_id: petition.id });
			},
		}),
		[petition, track, signValidationErrorTrackSampleRate],
	);
}
